import PageShell from '@/components/PageShell';
import { site } from '@/site.config';

type LegalKind = 'privacy' | 'terms';

interface LegalPageProps {
  kind: LegalKind;
}

const copy: Record<
  LegalKind,
  { title: string; updated: string; sections: { heading: string; body: string }[] }
> = {
  privacy: {
    title: 'Privacy Policy',
    updated: 'August 26, 2026',
    sections: [
      {
        heading: 'Overview',
        body: `This Privacy Policy describes how ${site.name} ("we", "us") collects, uses, and shares information when you visit ${site.domain} or contact us at ${site.contactEmail}. This is a starter policy for the site template — replace it with counsel-approved language before production use.`,
      },
      {
        heading: 'Information we collect',
        body: 'If you submit a contact form, we receive the details you provide (such as name, email, company, and message). We may also collect standard technical logs (IP address, browser type, pages viewed) from our hosting provider.',
      },
      {
        heading: 'How we use information',
        body: 'We use contact details to respond to inquiries and operate the site. We do not sell personal information. Analytics, if enabled later, will be disclosed here before activation.',
      },
      {
        heading: 'Contact',
        body: `Questions about this policy: ${site.contactEmail}.`,
      },
    ],
  },
  terms: {
    title: 'Terms of Service',
    updated: 'August 26, 2026',
    sections: [
      {
        heading: 'Agreement',
        body: `By using ${site.domain}, you agree to these Terms of Service. This is a starter document for the site template — replace it with counsel-approved terms before production use.`,
      },
      {
        heading: 'Services',
        body: `Content on this site is informational. Any engagement for paid services with ${site.name} is governed by a separate agreement.`,
      },
      {
        heading: 'Disclaimer',
        body: 'The site is provided as-is without warranties of any kind. We are not liable for damages arising from use of the site to the fullest extent permitted by law.',
      },
      {
        heading: 'Contact',
        body: `Questions about these terms: ${site.contactEmail}.`,
      },
    ],
  },
};

export default function LegalPage({ kind }: LegalPageProps) {
  const page = copy[kind];

  return (
    <PageShell>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-16 lg:py-24">
        <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Legal</p>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">{page.title}</h1>
        <p className="text-sm text-slate-500 mb-12">Last updated {page.updated}</p>
        <div className="flex flex-col gap-10">
          {page.sections.map((section) => (
            <section key={section.heading}>
              <h2 className="text-lg font-semibold text-white mb-3">{section.heading}</h2>
              <p className="text-slate-400 leading-relaxed text-[15px]">{section.body}</p>
            </section>
          ))}
        </div>
      </article>
    </PageShell>
  );
}
